import React, { useEffect, useState } from 'react';
import { deliveryAPI, PropertyData } from '../../../services/deliveryAPI';
import { parseApiError } from '../../../utils/apiErrors';
import PropertyAccessForm from '../../../components/BridgeClub/PropertyAccessForm';

const RED = '#B91C1C';

interface Props {
  onBack: () => void;
  onDone: () => void; // called after a successful save OR skip
}

/**
 * Step 4 — Property capture. Where the feed goes, gate/access notes, the dog, and photos.
 * SKIPPABLE: skipping sets property_capture_skipped so Tom knows to expect a longer survey
 * visit. A save stamps captured_at server-side; neither ever sets surveyed_at (that means a
 * driver has physically been). Photos are resized client-side before upload.
 *
 * THE FORM IS SHARED with the Profile's "Delivery access" section (PropertyAccessForm,
 * Brad 2026-09-23), so what a customer answers here is what they can change later.
 */
const PropertyCaptureStep: React.FC<Props> = ({ onBack, onDone }) => {
  const [form, setForm] = useState<PropertyData>({});
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const p = await deliveryAPI.getProperty();
        if (p && p.captured) setForm(p);
      } catch {
        /* first-time capture — nothing to prefill */
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await deliveryAPI.saveProperty(form);
      onDone();
    } catch (e) {
      // The server's reason survives - a gate code too long, a field it refused - with
      // the friendly line only as the fallback.
      setError(parseApiError(e, {
        fallback: 'Could not save your property details. Please try again.',
      }).message);
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    setSkipping(true);
    setError('');
    try {
      await deliveryAPI.skipProperty();
      onDone();
    } catch (e) {
      setError(parseApiError(e, {
        fallback: 'Could not skip right now. Please try again.',
      }).message);
    } finally {
      setSkipping(false);
    }
  };

  return (
    <div style={{ maxWidth: 620, margin: '0 auto', padding: '0 16px 32px' }}>
      <h2 style={{ color: RED, marginBottom: 4 }}>Property access</h2>
      <p style={{ color: '#555', marginTop: 0 }}>
        Help our driver find where the feed goes and get in safely. You can skip this and
        we'll walk your property with you on the first visit. You can change any of it later
        under Delivery access in your Profile.
      </p>

      {error && (
        <div style={{ background: '#fee', color: '#b91c1c', border: '1px solid #fcc', borderRadius: 8, padding: 10, margin: '8px 0' }}>
          {error}
        </div>
      )}

      <PropertyAccessForm form={form} onChange={setForm} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
        <button onClick={onBack} style={{ padding: '10px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer' }}>
          ← Back
        </button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={skip} disabled={skipping || saving} style={{ padding: '10px 18px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#6b7280', fontWeight: 600, cursor: 'pointer' }}>
            {skipping ? 'Skipping…' : 'Skip for now'}
          </button>
          <button onClick={save} disabled={saving || skipping} style={{ padding: '10px 20px', border: 'none', borderRadius: 8, background: RED, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save & continue →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyCaptureStep;
